import { useEffect } from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicyEn() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter','Noto Sans JP',sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/27619c500cc1b52bbdf164e0a3e622eb/58ff4382ad3eba0a2a097f9160e1ae7b.png"
              alt="Visipo Logo"
              className="h-7 w-auto object-contain"
            />
            <span className="font-bold text-lg text-gray-900" style={{ fontFamily: "'Inter', sans-serif" }}>Visipo</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-gray-500 hover:text-[#13B6EC] transition-colors flex items-center gap-1"
          >
            <i className="ri-arrow-left-line" />
            Back to Top
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-20">
        {/* Title */}
        <div className="mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold text-[#13B6EC] bg-[#13B6EC]/8 tracking-widest uppercase mb-4">
            PRIVACY POLICY
          </div>
          <h1
            className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-4"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-400">Date of Enactment: April 5, 2026</p>
        </div>

        {/* Policy content */}
        <div className="prose-policy" style={{ fontFamily: "'Inter', sans-serif" }}>
          <p>
            This Privacy Policy stipulates the handling of Users' personal information and privacy information in the smartphone application "Visipo" (hereinafter referred to as the "App") provided by the developer Nagao.dev (hereinafter referred to as the "Developer").
          </p>
          <p>
            The App is developed and operated prioritizing the protection of Users' privacy, in compliance with requirements such as Apple's "App Review Guidelines."
          </p>

          {/* Section 1 */}
          <h2>1. Data Collected and Purposes of Use</h2>
          <p>
            The App collects and uses the following data for the provision of various functions and personalization of the experience.
          </p>

          <h3>(1) Contact Information and ID</h3>
          <ul className="list-disc pl-5 mt-2 space-y-2">
            <li><strong>Types of Data Collected</strong>: Email address, User ID (anonymous ID provided by the authentication provider)</li>
            <li><strong>Purposes of Use</strong>: User authentication, account management (App functionality)</li>
            <li><strong>Linkage to User</strong>: Linked to the account.</li>
          </ul>

          <h3>(2) Health and Fitness Data</h3>
          <ul className="list-disc pl-5 mt-2 space-y-2">
            <li><strong>Types of Data Collected</strong>: Step count data (acquired from Apple HealthKit or Google Fit)</li>
            <li><strong>Purposes of Use</strong>: Calculation of route completion status, determination of achievement goals, and automatic generation of diaries (App functionality, product personalization)</li>
            <li><strong>Linkage to User</strong>: Linked to the account.</li>
          </ul>

          <h3>(3) User Content and Settings Information</h3>
          <ul className="list-disc pl-5 mt-2 space-y-2">
            <li><strong>Types of Data Collected</strong>: Diary data generated and saved within the App, User profile and settings information (gender, date of birth, diary tone/persona settings, etc.). Note that gender and date of birth are entered voluntarily by the User, and the main functions of the App can be used without entering them.</li>
            <li><strong>Purposes of Use</strong>: Provision, saving, and customization of the writing style of diaries (provision of functions, product personalization)</li>
            <li><strong>Linkage to User</strong>: Linked to the account.</li>
          </ul>

          <h3>(4) Payment and Subscription Information</h3>
          <ul className="list-disc pl-5 mt-2 space-y-2">
            <li><strong>Types of Data Collected</strong>: Subscription purchase history (receipt data). Detailed payment information such as credit card numbers is directly acquired and managed by app stores (Apple / Google), and the Developer does not acquire or store it.</li>
            <li><strong>Purposes of Use</strong>: Provision of the Paid Plan, confirmation of contract status, and provision of premium functions</li>
            <li><strong>Linkage to User</strong>: Linked to the account or the device's anonymous ID.</li>
          </ul>

          <div className="bg-blue-50 border-l-4 border-[#13B6EC] p-4 rounded-r-lg my-6">
            <p className="text-sm text-gray-600 m-0">
              Note: The App does not collect or store Users' "location information (GPS, etc.)" at all. Furthermore, the Developer does not collect diagnostic data (crash logs, etc.) individually linked to Users.
            </p>
          </div>

          {/* Section 2 */}
          <h2>2. Data Collection and Provision to Third Parties</h2>
          <p>
            The App uses the following third-party services for the purpose of providing functions (secure cloud storage of data, AI-based content generation). Data is processed via these services, but <strong>data is never shared for third-party advertising or marketing purposes.</strong>
          </p>

          <div className="space-y-4 my-6">
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-bold text-gray-900 text-sm mb-2">Firebase (Google LLC)</h4>
              <ul className="text-sm list-disc pl-5 space-y-1">
                <li><strong>Purpose of Use</strong>: User authentication (Firebase Authentication), cloud storage of diaries and progress data (Cloud Firestore)</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-bold text-gray-900 text-sm mb-2">Google Generative AI API (Google LLC)</h4>
              <ul className="text-sm list-disc pl-5 space-y-1">
                <li><strong>Purpose of Use</strong>: Dynamic generation of diary text by AI</li>
                <li><strong>Data Sent</strong>: Numerical step counts, profile information entered by the User on the settings screen (gender, date of birth, etc.), diary writing style settings (persona/tone). Direct personal identification information such as usernames and email addresses is not sent to the AI API.</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-bold text-gray-900 text-sm mb-2">Google Cloud Text-to-Speech API (Google LLC)</h4>
              <ul className="text-sm list-disc pl-5 space-y-1">
                <li><strong>Purpose of Use</strong>: Provision of voice reading function for diaries</li>
                <li><strong>Data Sent</strong>: Diary text, voice settings (voice quality, speed, pitch)</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-bold text-gray-900 text-sm mb-2">OpenWeatherMap API (OpenWeather Ltd)</h4>
              <ul className="text-sm list-disc pl-5 space-y-1">
                <li><strong>Purpose of Use</strong>: Acquisition of weather information during diary generation</li>
                <li><strong>Data Sent</strong>: Latitude and longitude coordinate information (to acquire the weather at the reached point on the virtual route within the App). Note that this is not the User's actual location information, but a fictional point on the map within the App. The coordinate information is used only at the time of the API call and is not saved on the Developer's servers.</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-bold text-gray-900 text-sm mb-2">Google Maps Geocoding API (Google LLC)</h4>
              <ul className="text-sm list-disc pl-5 space-y-1">
                <li><strong>Purpose of Use</strong>: Conversion of coordinates of the reached point on the virtual route into place names (city/town/village names, etc.)</li>
                <li><strong>Data Sent</strong>: Latitude and longitude coordinate information (a fictional point on the map within the App). The coordinate information is used only at the time of the API call and is not saved on the Developer's servers.</li>
              </ul>
            </div>
          </div>

          {/* Section 3 */}
          <h2>3. Tracking and Advertising</h2>
          <p>
            The App does not engage in "tracking" (Targeting/Tracking) that traces Users across other companies' apps or websites. Furthermore, acquired User data (including all email addresses and step data) is never provided, sold, or shared with advertising networks, data brokers, or other third parties.
          </p>

          {/* Section 4 */}
          <h2>4. Handling of HealthKit and Google Fit Data</h2>
          <p>
            The App reads healthcare data such as step counts using Apple's HealthKit API and Google's Google Fit API. These data are treated as highly sensitive information and are protected under the following strict rules:
          </p>
          <ol className="list-decimal pl-5 mt-2 space-y-2">
            <li>The acquired healthcare data is used solely to provide the main functions of the App (route completion based on step counts and diary generation).</li>
            <li>Healthcare data is never provided, sold, or used for tracking purposes by third-party advertising, advertising networks, data brokers, etc.</li>
          </ol>

          {/* Section 5 */}
          <h2>5. Data of Minors</h2>
          <p>
            The App is not a service directed at children under the age of 13, and it does not intentionally collect personal information from children under the age of 13. If it is discovered that the App has been used by a child under the age of 13, the Developer will promptly delete the corresponding data.
          </p>

          {/* Section 6 */}
          <h2>6. Data Storage Location and Retention Period</h2>
          <ol className="list-decimal pl-5 mt-2 space-y-2">
            <li>Data collected by the App is stored on cloud servers operated by Google LLC (which may be located outside of Japan, including the United States).</li>
            <li>Users' personal information and User Content are retained while the User holds an account, and are promptly deleted after the account is deleted.</li>
          </ol>

          {/* Section 7 */}
          <h2>7. Data Management and Deletion</h2>
          <p>
            Users can access and check the contents of their own personal information and User Content at any time through the functions of the App.
          </p>
          <p>
            Additionally, if a User performs the "delete account (withdraw)" procedure from the settings within the App, all linked data such as the User's account information, step count information, and diary data saved on the Developer's cloud database will be promptly invalidated and deleted.
          </p>

          {/* Section 8 */}
          <h2>8. Changes to the Privacy Policy</h2>
          <p>
            The Developer may revise this Privacy Policy as necessary due to changes in laws and regulations, additions or changes to the functions of the App, etc. If there are material changes concerning the purposes of use, the Developer will notify Users by an appropriate method, such as an in-app notice, and re-obtain the necessary consent before applying the changes.
          </p>

          {/* Section 9 */}
          <h2>9. Contact Information</h2>
          <p>
            If you have any questions regarding this Privacy Policy or the handling of personal information, or if you wish to request data deletion, please contact the following inquiry counter:
          </p>
          <div className="bg-gray-50 rounded-xl p-5 my-4">
            <ul className="text-sm list-disc pl-5 space-y-1">
              <li><strong>Developer</strong>: Nagao.dev</li>
              <li><strong>Email Address</strong>: support@visipo.com</li>
              <li>Or please contact us from the "Contact Us" menu within the App.</li>
            </ul>
          </div>
        </div>

        {/* Back button */}
        <div className="mt-16 pt-8 border-t border-gray-100 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#13B6EC] text-sm font-semibold hover:underline"
          >
            <i className="ri-arrow-left-line" />
            Back to Top
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center" style={{ background: "#1a2332" }}>
        <p className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
          &copy; 2026 Visipo. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
